import { contractsService } from 'api/services/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CONTRACT_STATUS, DEFAULT_FILTERS, PAGE_CONFIG } from '../constants';
import { formatContractData, SCROLL_THRESHOLD_PX } from '../utils/helper';

// Constants
const { ACTIVE } = CONTRACT_STATUS;
const { PAGE_SIZE, PAGE_NO } = PAGE_CONFIG;

const useContract = (initialFilters = {}) => {
  // Filter state management (moved from context)
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  // State management
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(PAGE_NO);

  // Memoized formatters and utilities

  // API request builder
  const buildApiParams = useCallback(
    (page, overrideFilters = {}) => ({
      page,
      limit: PAGE_SIZE,
      ...filters,
      ...overrideFilters,
      tech_stack:
        overrideFilters.tech_stack?.join(',') || filters.tech_stack?.join(','),
      status: overrideFilters.status || filters.status || ACTIVE,
    }),
    [filters]
  );

  // Process API response
  const processApiResponse = useCallback((response, append = false) => {
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch contracts');
    }

    const contractsData = response.data.data || response.data;
    const contractsArray = Array.isArray(contractsData) ? contractsData : [];
    const formattedContracts = contractsArray.map(formatContractData);

    setContracts((prev) =>
      append ? [...prev, ...formattedContracts] : formattedContracts
    );

    // Use pagination data from API response instead of guessing
    const totalPages = response.data.total_pages || 1;
    const currentPage = response.data.page || 1;
    setHasMore(currentPage < totalPages);

    return formattedContracts;
  }, []);

  // Fetch contracts with error handling
  const fetchContracts = useCallback(
    async (page = PAGE_NO, append = false, overrideFilters = {}) => {
      try {
        // Set appropriate loading state
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        // Clear previous errors only for new requests
        if (!append) {
          setError(null);
        }

        const apiParams = buildApiParams(page, overrideFilters);
        const response = await contractsService.getContracts(apiParams);

        await processApiResponse(response, append);
        setCurrentPage(page);
      } catch (err) {
        console.error('Error fetching contracts:', err);
        setError(err.message || 'Failed to load contracts');
      } finally {
        // Only reset the loading state that was set
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [buildApiParams, processApiResponse]
  );

  // Load more contracts
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    fetchContracts(currentPage + 1, true);
  }, [isLoadingMore, hasMore, currentPage, fetchContracts]);

  // Infinite scroll handler with debouncing
  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    const { scrollHeight, scrollTop, clientHeight } = document.documentElement;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD_PX) {
      loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  // Ref for debounced scroll timeout to prevent memory leaks
  const scrollTimeoutRef = useRef(null);

  // Track initial fetch and previous search value
  const hasFetchedRef = useRef(false);
  const prevSearchRef = useRef(filters?.search);

  // Debounced scroll handler to prevent multiple rapid calls
  const debouncedScrollHandler = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(handleScroll, 200); // 200ms debounce
  }, [handleScroll]);

  // Ref to store the current scroll handler to avoid listener recreation
  const scrollHandlerRef = useRef(null);

  // Update ref when handler changes
  useEffect(() => {
    scrollHandlerRef.current = debouncedScrollHandler;
  }, [debouncedScrollHandler]);

  // Set up scroll listener only once
  useEffect(() => {
    const handler = () => scrollHandlerRef.current?.();
    window.addEventListener('scroll', handler, { passive: true });
    return () => {
      window.removeEventListener('scroll', handler);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Refresh contracts
  const refreshContracts = useCallback(
    (refreshFilters) => {
      setCurrentPage(PAGE_NO);
      fetchContracts(PAGE_NO, false, refreshFilters);
    },
    [fetchContracts]
  );

  // Initial fetch on mount (runs once)
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchContracts(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  // Debounced search effect - only triggers on actual search changes
  useEffect(() => {
    // Skip if search hasn't actually changed (prevents double fetch on mount)
    if (prevSearchRef.current === filters?.search) {
      return;
    }
    prevSearchRef.current = filters?.search;

    const timeoutId = setTimeout(() => {
      fetchContracts(1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters?.search, fetchContracts]);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  return {
    // State
    contracts,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    currentPage,

    // Filter state
    filters,
    setFilters,

    // Computed values
    filteredContracts: contracts,

    // Actions
    fetchContracts,
    loadMore,
    refreshContracts,
    setError,
    clearError,
  };
};

export default useContract;
